import {ChevronRightIcon, DownloadIcon} from '@heroicons/react/solid';
import type {JSX} from 'react';
import {Link} from 'react-router-dom';

import {Button, Pagination, TableSkeleton} from '@/components';
import Badge from '@/components/common/Badge';
import type {PaginationProps} from '@/components/common/Pagination';
import {Table, TBody, Td, Tr} from '@/components/Table/Table';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {AfriexTransactionType, statusColors} from '@/types/Transaction';
import {capitalizeWords, getBusinessTxAmountAndAsset} from '@/utils/dashboard';

/**
 * Rows per page in the Recent History table.
 *
 * Exported so the page's fetch and the footer agree on one page size: the
 * footer computes `ceil(total / limit)`, so a fetch that used a different
 * `limit` would show the wrong number of pages.
 */
export const BUSINESS_HISTORY_PAGE_SIZE = 10;

type BusinessTransactionHistoryProp = {
  data: AfriexBusinessTransaction[];
  handleExport: () => void;
  loadingExport: boolean;
  totalTransactions: number;
  /**
   * A page fetch is in flight. Swaps the rows for the shared `TableSkeleton`
   * while keeping the header and pagination footer mounted, so a page change
   * never flashes the previous page's rows (or remounts `Pagination`, whose
   * mount effect would snap the view back to page 1).
   */
  isLoading?: boolean;
  /**
   * Asked to load a page. Omit to render the table without pagination (the
   * footer only appears when this is provided).
   */
  onPageChange?: PaginationProps['onPageChange'];
  /**
   * 0-indexed page the rows are from. Keeps the footer's own page number in
   * step when the page reloads the history itself (e.g. back to the first
   * page after following a row to another transaction), which the footer
   * cannot see.
   */
  page?: number;
  /** Heading, e.g. whose history this is. */
  title?: string;
};

export default function BusinessTransactionHistory({
  data,
  handleExport,
  loadingExport,
  totalTransactions,
  isLoading = false,
  onPageChange,
  page,
  title = 'Recent History',
}: BusinessTransactionHistoryProp) {
  return (
    <section>
      <div className="w-full flex justify-between font-semibold items-center">
        <h2 className="font-semibold text-lg">{title}</h2>

        <div className="text-cyan-700 flex">
          <Button
            variant="ghost"
            colorScheme="cyan"
            leftIcon={<DownloadIcon className="h-5 w-5" />}
            className="flex items-center"
            isLoading={loadingExport}
            onClick={handleExport}
            loadingText="exporting"
            disabled={totalTransactions <= 0}
          >
            Export as .CSV
          </Button>
        </div>
      </div>
      <hr className="border-none mt-3 w-full dark:border-dark-borderMid" />
      <div className="hide-scrollbar">
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <BusinessTransactionHistoryTable data={data} />
        )}
      </div>
      {/* Pages the rows this table shows. The export is independent: the
          server streams the whole history, whatever page is shown here. */}
      {onPageChange && data.length > 0 && (
        <div>
          <Pagination
            onPageChange={onPageChange}
            total={totalTransactions}
            limit={BUSINESS_HISTORY_PAGE_SIZE}
            currentPageOverride={page === undefined ? undefined : page + 1}
          />
        </div>
      )}
    </section>
  );
}

const BusinessTransactionHistoryTable = ({
  data,
}: {
  data: AfriexBusinessTransaction[];
}): JSX.Element => {
  return (
    <Table>
      <TBody className="!divide-y-0">
        {data.map((transaction) => {
          const redirectLink = `/merchants/transactions/${transaction.id}`;
          const {
            txType,
            initiatedAmount,
            initiatedAsset,
            fulfilledAmount,
            fulfilledAsset,
          } = getBusinessTxAmountAndAsset(transaction, 'rate');
          const displayAmount =
            txType === AfriexTransactionType.WITHDRAW
              ? fulfilledAmount
              : initiatedAmount;
          const displayAsset =
            txType === AfriexTransactionType.WITHDRAW
              ? fulfilledAsset
              : initiatedAsset;

          return (
            <Tr
              key={transaction.id}
              data-cy="merchant-transaction-history-row"
              data-transaction-id={transaction.id}
            >
              <Td className="ps-5">
                {displayAmount} {displayAsset}
              </Td>
              <Td>
                <Badge
                  label={transaction.status || 'N/A'}
                  colorScheme={
                    transaction.status ? statusColors[transaction.status] : ''
                  }
                />
              </Td>
              <Td>{capitalizeWords(transaction.type?.replace(/_/g, ' '))}</Td>
              <Td>{new Date(transaction.createdAt).toLocaleDateString()}</Td>
              <Td className="hover:text-cyan-500">
                <Link
                  to={redirectLink}
                  data-cy="merchant-transaction-history-details-link"
                >
                  <ChevronRightIcon className=" w-5 h-5" />
                </Link>
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
};
